import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, PARTNER_DISABLED } from "@/lib/api";
import { GenisLogo } from "@/components/GenisLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { markWelcome } from "@/components/WelcomeBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Lock, Mail, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.pexels.com/photos/19392859/pexels-photo-19392859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1300&w=1400";
const TICKER = ["Résidentiel · BAR", "Tertiaire · BAT", "Industrie · IND", "Agriculture · AGRI", "Réseaux · RES", "Coup de pouce", "Contrat de performance énergétique", "Zones non interconnectées"];

const Ticker = () => (
  <div className="relative overflow-hidden border-t border-border py-4 [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
    <div className="flex w-max animate-marquee gap-8 eyebrow text-muted-foreground">
      {[...TICKER, ...TICKER].map((t, i) => (
        <span key={i} className="flex items-center gap-8 whitespace-nowrap">{t}<span className="h-1 w-1 rounded-full bg-genis" /></span>
      ))}
    </div>
  </div>
);

const Hero = () => (
  <div className="relative min-w-0 flex flex-col justify-between lg:min-h-screen px-6 sm:px-12 xl:px-16 pt-10 pb-0">
    <div className="flex items-center justify-between animate-fade-up">
      <GenisLogo size="md" />
      <ThemeToggle className="lg:hidden" />
    </div>
    <div className="py-10 lg:py-14 max-w-xl">
      <span className="inline-flex items-center gap-2 rounded-full border border-genis/30 bg-genis/5 px-3 py-1.5 eyebrow text-genis animate-fade-up" style={{ animationDelay: "80ms" }}>
        <span className="h-1.5 w-1.5 rounded-full bg-genis animate-pulse-dot" /> Calculette CEE · Certificats d'économies d'énergie
      </span>
      <h1 className="mt-7 text-5xl sm:text-6xl xl:text-7xl font-bold tracking-tight leading-[0.95] animate-fade-up" style={{ animationDelay: "160ms" }} data-testid="login-hero-title">
        L'efficacité énergétique,<br /><span className="text-genis">valorisée.</span>
      </h1>
      <p className="mt-7 text-base text-muted-foreground leading-relaxed animate-fade-up" style={{ animationDelay: "240ms" }}>
        Calculez vos primes CEE, gérez vos dossiers opération par opération et générez vos devis en PDF — depuis un espace unique.
      </p>
    </div>
    <Ticker />
  </div>
);

const LoginCard = ({ onSubmit, loading, email, setEmail, password, setPassword }) => {
  const [show, setShow] = useState(false);
  return (
    <form onSubmit={onSubmit} className="glass w-full max-w-[420px] rounded-3xl border border-white/10 dark:border-white/10 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] animate-fade-up" style={{ animationDelay: "200ms" }} data-testid="login-card">
      <div className="flex items-center gap-3.5">
        <span className="h-11 w-11 grid place-items-center rounded-xl border border-genis/30 bg-genis/10 text-genis"><Lock className="h-5 w-5" /></span>
        <div>
          <h2 className="text-xl font-semibold leading-tight">Accès à votre espace</h2>
          <p className="text-xs text-muted-foreground">Réservé à Genis Energie et ses partenaires</p>
        </div>
      </div>
      <div className="mt-8 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="eyebrow text-muted-foreground">Adresse email</Label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.fr" className="h-12 pl-10 rounded-xl bg-background/70 focus-visible:ring-genis" data-testid="login-email-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="eyebrow text-muted-foreground">Mot de passe</Label>
          <div className="relative">
            <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="password" type={show ? "text" : "password"} required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" className="h-12 pl-10 pr-11 rounded-xl bg-background/70 focus-visible:ring-genis" data-testid="login-password-input" />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground transition-colors" data-testid="login-password-toggle">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="group w-full h-12 rounded-xl bg-genis hover:bg-genis-dark text-white text-base font-semibold shadow-[0_10px_30px_-8px_rgb(var(--genis)/0.7)] transition-[background-color,transform] active:scale-[0.98]" data-testid="login-submit-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-1" /></>}
        </Button>
      </div>
      <p className="mt-7 pt-5 border-t border-border text-xs text-muted-foreground leading-relaxed" data-testid="login-no-account-hint">
        Pas de compte ? Contactez votre administrateur Genis Energie pour obtenir vos accès. Votre espace (Genis ou partenaire) est reconnu automatiquement.
      </p>
    </form>
  );
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("cee_disabled")) {
      sessionStorage.removeItem("cee_disabled");
      setTimeout(() => toast.error(PARTNER_DISABLED, { duration: 8000 }), 100);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      markWelcome();
      toast.success("Connexion réussie");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]" data-testid="login-page">
      <Hero />
      <div className="relative min-w-0 min-h-[640px] lg:min-h-screen flex items-center justify-center p-6 pb-20 overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/20 dark:via-background/80 dark:to-background/40" />
        <div className="hidden lg:block absolute top-8 right-8 z-10"><ThemeToggle /></div>
        <LoginCard onSubmit={submit} loading={loading} email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
        <div className="absolute bottom-6 right-8 flex items-center gap-3 rounded-full glass border border-border pl-4 pr-1.5 py-1.5 eyebrow text-[10px] text-muted-foreground">
          Propulsé par <span className="rounded-full bg-black/85 px-3 py-1"><GenisLogo size="sm" forceLight /></span>
        </div>
      </div>
    </div>
  );
}
